/**
 * The reader's project list, kept on this device so the picker, the
 * dashboard's banner and title, and the page header draw at once on a cold
 * launch instead of waiting on `/api/projects` (Andrew, 2026-10-06: "cache the
 * projects list on the device too").
 *
 * DISPLAY ONLY — never the query cache. This copy is drawn while the live list
 * is still loading, and replaced by it the moment it lands. It is deliberately
 * NOT seeded into React Query (`initialData` / `placeholderData`): the same
 * key feeds Project Settings' forms, the route guards and the billing tab, and
 * a form initialised from a day-old copy would save day-old values. Here the
 * worst a stale copy can do is show a renamed project's old name for the
 * length of one request.
 *
 * ONE ACCOUNT. The copy names the user it belongs to, and nothing is read
 * until AuthContext has said who is signed in (`setProjectsSnapshotOwner`) and
 * it is the same person — so a shared sales-office machine whose last session
 * merely expired never shows the next person someone else's projects. It is
 * removed at sign-out along with the rest of the per-user state.
 *
 * Only what those surfaces draw is kept: names, codes, city, type, state, the
 * builder names and the logo and cover PATHS (public objects, the builders'
 * logos among them). Nothing a form reads, no settings, no counts.
 */

const STORE = "salefish:projects:v1";
/** The image-paths-only list this replaced (2026-10-06); removed when this is written. */
const LEGACY_STORE = "salefish:projectImages:v1";
const MAX_PROJECTS = 200;

export type SnapshotProject = {
  id: string;
  projectName: string;
  projectLogo: string | null;
  projectCoverImage: string | null;
  municipality: string | null;
  /** "ON": printed after the city on a card. */
  province?: string | null;
  projectType: string | null;
  projectId: string | null;
  state: string | null;
  isTestProject: boolean;
  companyId: string | null;
  companyIds: string[];
};

/** A builder: its name, and its logo's path for the card's builder mark (a public object). */
export type SnapshotCompany = { id: string; companyName: string; logoUrl?: string | null };

type Stored = { userId: string; projects: SnapshotProject[]; companies: SnapshotCompany[] };

/** Who AuthContext says is signed in, this page load. Null until it knows. */
let owner: string | null = null;

export function setProjectsSnapshotOwner(userId: string | null | undefined): void {
  owner = userId ?? null;
}

/** A same-origin path, never `data:`/`blob:`/protocol-relative. */
const path = (s: unknown): string | null =>
  typeof s === "string" && s.startsWith("/") && !s.startsWith("//") ? s : null;
const text = (s: unknown): string | null => (typeof s === "string" && s ? s : null);

function readStored(): Stored | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORE) ?? "null");
    if (!parsed || typeof parsed.userId !== "string" || !Array.isArray(parsed.projects)) return null;
    return { userId: parsed.userId, projects: parsed.projects, companies: Array.isArray(parsed.companies) ? parsed.companies : [] };
  } catch {
    return null;
  }
}

function write(stored: Stored): void {
  try {
    const next = JSON.stringify(stored);
    if (localStorage.getItem(STORE) !== next) localStorage.setItem(STORE, next);
    localStorage.removeItem(LEGACY_STORE);
  } catch {
    /* private mode or full storage — the next launch waits for the network, as before */
  }
}

/** Keep the list the app just loaded for `userId`. Archived projects are kept: the picker filters them itself. */
export function saveProjectsSnapshot(userId: string, projects: ReadonlyArray<Record<string, any>>): void {
  const prev = readStored();
  const rows: SnapshotProject[] = projects
    .filter((p) => p && typeof p.id === "string")
    .slice(0, MAX_PROJECTS)
    .map((p) => ({
      id: p.id,
      projectName: typeof p.projectName === "string" ? p.projectName : "",
      projectLogo: path(p.projectLogo),
      projectCoverImage: path(p.projectCoverImage),
      municipality: text(p.municipality),
      province: text(p.province),
      projectType: text(p.projectType),
      projectId: text(p.projectId),
      state: text(p.state),
      isTestProject: !!p.isTestProject,
      companyId: text(p.companyId),
      companyIds: Array.isArray(p.companyIds) ? p.companyIds.filter((c: unknown): c is string => typeof c === "string") : [],
    }));
  write({ userId, projects: rows, companies: prev?.userId === userId ? prev.companies : [] });
}

/** Keep the builder names the picker and header put on each project. */
export function saveCompaniesSnapshot(userId: string, companies: ReadonlyArray<Record<string, any>>): void {
  const prev = readStored();
  if (!prev || prev.userId !== userId) return; // names without their projects are no use
  const wanted = new Set(prev.projects.flatMap((p) => [p.companyId, ...p.companyIds]).filter(Boolean));
  const rows = companies
    .filter((c) => c && typeof c.id === "string" && wanted.has(c.id))
    .map((c) => ({ id: c.id as string, companyName: typeof c.companyName === "string" ? c.companyName : "", logoUrl: path(c.logoUrl) }));
  write({ ...prev, companies: rows });
}

/**
 * The signed-in reader's copy, or null — when nobody is known to be signed in,
 * or the copy is someone else's. `userId` is the caller's own answer from the
 * server (the picker's and the dashboard's user query); without one, the
 * account AuthContext reported.
 */
export function readProjectsSnapshot(userId?: string | null): { projects: SnapshotProject[]; companies: SnapshotCompany[] } | null {
  const who = userId ?? owner;
  if (!who) return null;
  const stored = readStored();
  if (!stored || stored.userId !== who || stored.projects.length === 0) return null;
  return { projects: stored.projects, companies: stored.companies };
}

/**
 * Image paths only, with no owner check — for the boot warm-up, which runs
 * before anyone is known and only fetches public objects (it shows nothing).
 */
export function readSnapshotImagePaths(): Array<{ id: string; projectName: string; isTestProject: boolean; state: string | null; logo: string | null; cover: string | null }> {
  const stored = readStored();
  if (!stored) return [];
  return stored.projects.map((p) => ({
    id: p.id,
    projectName: p.projectName,
    isTestProject: p.isTestProject,
    state: p.state,
    logo: path(p.projectLogo),
    cover: path(p.projectCoverImage),
  }));
}

export function forgetProjectsSnapshot(): void {
  owner = null;
  try {
    localStorage.removeItem(STORE);
    localStorage.removeItem(LEGACY_STORE);
  } catch {
    /* nothing to forget */
  }
}
